import { Box, IconButton, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { CopyToClipboardButton, DynamicIcon, Tooltip } from 'src/components';

export type LiveLinkState =
  | { status: 'checking' }
  | { status: 'failed'; retry: () => void }
  | { status: 'notLive' }
  | { status: 'live'; url: string };

const COPY: Record<
  Exclude<LiveLinkState['status'], 'live'>,
  { label: string; text: string }
> = {
  checking: { label: 'Checking', text: 'Looking up the live link' },
  failed: { label: 'Unknown', text: "Couldn't check the link" },
  notLive: { label: 'Not live', text: 'Save the page to publish it' },
};

const liveFieldSx: SxProps<Theme> = {
  flex: '1 1 16.25rem',
  minWidth: 0,
  height: '2rem',
  px: 1.5,
  borderRadius: 1,
  border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
};

const statusDotSx = (status: LiveLinkState['status']): SxProps<Theme> => ({
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: (theme) => {
    if (status === 'live') return theme.palette.success.main;
    if (status === 'failed') return theme.palette.warning.main;
    return theme.palette.text.disabled;
  },
});

const LiveLinkActions = ({ state }: { state: LiveLinkState }) => {
  if (state.status === 'failed') {
    return (
      <Tooltip title="Check again">
        <IconButton size="small" onClick={state.retry} aria-label="Check again" sx={{ p: 0 }}>
          <DynamicIcon name="refresh" sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    );
  }

  if (state.status !== 'live') return null;

  return (
    <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
      <CopyToClipboardButton
        content={state.url}
        tooltipText="Copy live page link"
        successMessage="Live page link copied"
      />
      <Tooltip title="Open live page">
        <IconButton
          size="small"
          href={state.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open live page"
          sx={{ p: 0 }}
        >
          <DynamicIcon name="open_in_new" sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Wraps above the actions once the pane is too narrow to show the address beside them.
export const LiveLink = ({ state }: { state: LiveLinkState }) => {
  const isLive = state.status === 'live';
  const { label, text } = isLive
    ? { label: 'Live', text: state.url.replace(/^https?:\/\//, '') }
    : COPY[state.status];

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={liveFieldSx}>
      <Box sx={statusDotSx(state.status)} />
      <Typography variant="caption" fontWeight={600} color="text.secondary" flexShrink={0}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        color={isLive ? 'text.primary' : 'text.secondary'}
        noWrap
        flex={1}
        minWidth={0}
      >
        {text}
      </Typography>
      <LiveLinkActions state={state} />
    </Stack>
  );
};
